import { useState, useCallback } from 'react';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';

interface WalletsFiltersState {
  currency: string;
  balanceMin: string;
  balanceMax: string;
}

export const useWalletsFilters = ({
  initialConditions,
  onChange,
}: {
  initialConditions: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}) => {
  const parseConditions = (conditions: FilterConditionOutput[]): WalletsFiltersState => {
    const state: WalletsFiltersState = { currency: '', balanceMin: '', balanceMax: '' };
    conditions.forEach((condition) => {
      if (condition.column === 'currency' && condition.action === 'contains') {
        state.currency = condition.value as string;
      }
      if (condition.column === 'balance_total' && condition.action === 'greater_equal') {
        state.balanceMin = condition.value as string;
      }
      if (condition.column === 'balance_total' && condition.action === 'less_equal') {
        state.balanceMax = condition.value as string;
      }
    });
    return state;
  };

  const [filters, setFilters] = useState<WalletsFiltersState>(() => parseConditions(initialConditions));

  const convertToConditions = useCallback((state: WalletsFiltersState) => {
    const conditions: FilterConditionOutput[] = [];
    if (state.currency) {
      conditions.push({ column: 'currency', action: 'contains', value: state.currency });
    }
    if (state.balanceMin) {
      conditions.push({ column: 'balance_total', action: 'greater_equal', value: state.balanceMin });
    }
    if (state.balanceMax) {
      conditions.push({ column: 'balance_total', action: 'less_equal', value: state.balanceMax });
    }
    return conditions;
  }, []);

  const handleFilterChange = (key: keyof WalletsFiltersState, value: string) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      onChange?.(convertToConditions(next));
      return next;
    });
  };

  const clearFilters = () => {
    const next = { currency: '', balanceMin: '', balanceMax: '' };
    setFilters(next);
    onChange?.(convertToConditions(next));
  };

  return { filters, handleFilterChange, clearFilters };
};
